import React from 'react';
import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import type { ICampaign } from '../../../redux/buzzbridge/types';
import { BRAND_SURFACE_CONTAINER_LOW, BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import { FONT_ENUM } from '../../../types/global/fonts.types';

export type CampaignManagePanel =
    | 'deliverables'
    | 'targeting'
    | 'brief'
    | 'assets'
    | 'videos'
    | 'team'
    | 'add_creators';

interface Props {
    campaign: ICampaign;
    onOpen: (panel: CampaignManagePanel) => void;
    /** Hide Add creators for team members without wallet access, etc. */
    showAddCreators?: boolean;
}

export function CampaignManageGrid({ campaign, onOpen, showAddCreators = true }: Props) {
    const deliverableCount = (campaign.deliverables || []).length;
    const roundCount = ((campaign as any).deliverable_rounds || []).length;
    const isRecurrent = Boolean((campaign as any).recurrent_deliverables);
    const deliverableSub = isRecurrent
        ? `${roundCount} round${roundCount !== 1 ? 's' : ''}`
        : `${deliverableCount} deliverable${deliverableCount !== 1 ? 's' : ''}`;

    const platformCount = (campaign.platforms || []).length;
    const tierLabel = campaign.influencer_tier
        ? campaign.influencer_tier.charAt(0).toUpperCase() + campaign.influencer_tier.slice(1)
        : '—';
    const targetingSub = `${tierLabel} · ${platformCount} platform${platformCount !== 1 ? 's' : ''}`;

    const objectiveCount = (campaign.objectives || []).filter(Boolean).length;
    const requirementCount = (campaign.requirements || []).filter(Boolean).length;
    const briefSub = `${objectiveCount} objective${objectiveCount !== 1 ? 's' : ''} · ${requirementCount} requirement${
        requirementCount !== 1 ? 's' : ''
    }`;

    const assetCount = (campaign.assets || []).length;
    const videoCount = (campaign.sample_videos || []).length;

    const seatCap = Number(campaign.total_participants_count) || 0;
    const canAddCreators =
        showAddCreators &&
        Boolean(campaign.funded) &&
        !campaign.funding_paused &&
        campaign.campaign_type !== 'commission' &&
        !(campaign as { app_install?: { enabled?: boolean } }).app_install?.enabled &&
        campaign.status !== 'closed';

    const cards: { id: CampaignManagePanel; icon: React.ReactNode; label: string; sub: string }[] = [
        {
            id: 'deliverables',
            icon: <ListAltOutlinedIcon sx={{ fontSize: 20 }} />,
            label: 'Deliverables',
            sub: deliverableSub,
        },
        {
            id: 'targeting',
            icon: <TuneOutlinedIcon sx={{ fontSize: 20 }} />,
            label: 'Targeting',
            sub: targetingSub,
        },
        {
            id: 'brief',
            icon: <ArticleOutlinedIcon sx={{ fontSize: 20 }} />,
            label: 'Content Brief',
            sub: briefSub,
        },
        {
            id: 'assets',
            icon: <PermMediaOutlinedIcon sx={{ fontSize: 20 }} />,
            label: 'Assets',
            sub: `${assetCount} file${assetCount !== 1 ? 's' : ''}`,
        },
        {
            id: 'videos',
            icon: <VideoLibraryOutlinedIcon sx={{ fontSize: 20 }} />,
            label: 'Sample Videos',
            sub: `${videoCount} video${videoCount !== 1 ? 's' : ''}`,
        },
        {
            id: 'team',
            icon: <GroupsOutlinedIcon sx={{ fontSize: 20 }} />,
            label: 'Team',
            sub: 'Admins, editors, viewers',
        },
        ...(canAddCreators
            ? [
                  {
                      id: 'add_creators' as const,
                      icon: <PersonAddAlt1OutlinedIcon sx={{ fontSize: 20 }} />,
                      label: 'Add creators',
                      sub: seatCap > 0 ? `${seatCap} seats · lock from wallet` : 'Lock escrow for more seats',
                  },
              ]
            : []),
    ];

    return (
        <Box>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 12,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.35)',
                    mb: 1.5,
                }}>
                Manage campaign
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 1.5,
                }}>
                {cards.map(card => (
                    <Box
                        key={card.id}
                        onClick={() => onOpen(card.id)}
                        sx={{
                            bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                            borderRadius: 2,
                            p: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            border: '1px solid rgba(255,255,255,0.04)',
                            transition: 'border-color 0.15s, background-color 0.15s',
                            '&:hover': {
                                borderColor: 'rgba(255,201,101,0.3)',
                                bgcolor: 'rgba(255,201,101,0.03)',
                            },
                        }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ color: BRAND_LUXE_PRIMARY }}>{card.icon}</Box>
                            <ChevronRightIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.25)' }} />
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 13,
                                    color: '#fff',
                                    lineHeight: 1.3,
                                }}>
                                {card.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: 'rgba(255,255,255,0.4)',
                                    mt: 0.4,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    lineHeight: 1.4,
                                }}>
                                {card.sub}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
